import type { TimelineCameraFrame } from "./session";
import type { TimelineViewTransform } from "../ViewTransform/transform";

export interface RestoredCameraSeed {
  view: TimelineViewTransform;
  followZoom: number;
  lockAutoFit: boolean;
  panHeld: boolean;
  /** Keep the saved frame until playback or a camera gesture. */
  holdUntilPlayback: boolean;
}

const DEFAULT_VIEW: TimelineViewTransform = { zoom: 1, panX: 0, panY: 0 };

export function seedRestoredCamera(
  camera: TimelineCameraFrame | null,
): RestoredCameraSeed {
  if (camera === null) {
    return {
      view: DEFAULT_VIEW,
      followZoom: 1,
      lockAutoFit: true,
      panHeld: false,
      holdUntilPlayback: false,
    };
  }
  return {
    view: { zoom: camera.zoom, panX: camera.panX, panY: camera.panY },
    followZoom: camera.heldZoom ?? camera.zoom,
    lockAutoFit: camera.heldZoom === null && !camera.panHeld,
    panHeld: camera.panHeld,
    holdUntilPlayback: true,
  };
}

export function toPersistedCameraFrame(
  cameraLockEnabled: boolean,
  view: TimelineViewTransform,
  lockAutoFit: boolean,
  followZoom: number,
  panHeld: boolean,
): TimelineCameraFrame {
  const heldPan = cameraLockEnabled && panHeld;
  return {
    ...view,
    heldZoom:
      cameraLockEnabled && !lockAutoFit && !heldPan ? followZoom : null,
    panHeld: heldPan,
  };
}

export function noteAutoZoomChange(
  seen: { current: boolean },
  enabled: boolean,
  lockAutoFit: { current: boolean },
  lastFitMs: { current: number },
  restoredHold: { current: boolean },
  cameraLockEnabled: boolean,
): void {
  if (seen.current !== enabled) {
    const turnedOn = enabled && !seen.current;
    seen.current = enabled;
    lockAutoFit.current = enabled;
    if (turnedOn) {
      lastFitMs.current = 0;
    }
  }
  if (cameraLockEnabled && enabled) {
    restoredHold.current = false;
  }
}

export function keepUnlockedFrame(
  cameraLockEnabled: boolean,
  wasLocked: boolean,
  viewRef: { current: TimelineViewTransform },
  pending: { current: TimelineViewTransform | null },
  zoom: number,
  panX: number,
  panY: number,
): void {
  if (cameraLockEnabled) {
    pending.current = null;
    return;
  }
  if (wasLocked) {
    pending.current = { ...viewRef.current };
    return;
  }
  if (pending.current === null) {
    viewRef.current = { zoom, panX, panY };
    return;
  }
  if (
    zoom === viewRef.current.zoom
    && panX === viewRef.current.panX
    && panY === viewRef.current.panY
  ) {
    pending.current = null;
    return;
  }
}

/** A refresh should show the saved frame until the story is playing again. */
export function shouldHoldRestoredCamera(
  holdUntilPlayback: boolean,
  playing: boolean,
  scrub: number,
): boolean {
  return holdUntilPlayback && !playing && scrub === 0;
}
